import { useEffect, useRef, useState } from 'react'
import { Plus } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useOnboarding, useForwardAction } from '@/components/onboarding'
import { useProviderAuth } from '@/hooks/auth'
import {
  useVisitTypes,
  useCreateVisitType,
} from '@/hooks/useVisitTypes'
import { VisitTypeModal } from '@/components/visit-types/VisitTypeModal'
import { formatVisitTypeCategory } from '@/lib/format'
import type { VisitType, VisitTypeCategory } from '@/types/settings'

const STEP_INDEX = 6

interface DefaultVisitType {
  name: string
  category: VisitTypeCategory
  price_cents: number
  description: string
}

const PLATFORM_DEFAULT_VISIT_TYPES: DefaultVisitType[] = [
  {
    name: 'Initial Video Consultation',
    category: 'initial',
    price_cents: 7500,
    description:
      'First-time video consultation for new patients with skin concerns.',
  },
  {
    name: 'Video Follow-Up',
    category: 'follow_up',
    price_cents: 5000,
    description:
      'Follow-up video visit to review treatment progress and adjust care.',
  },
  {
    name: 'Prescription Refill',
    category: 'refill',
    price_cents: 3000,
    description: 'Quick visit to renew an existing prescription.',
  },
]

function formatDollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`
}

export function VisitTypesStep() {
  const { setStepData, markCompleted, goToStep } = useOnboarding()
  const { provider } = useProviderAuth()
  const { data: visitTypes, isSuccess } = useVisitTypes()
  const createVisitType = useCreateVisitType()

  const [modalState, setModalState] = useState<{ open: boolean; visitType: VisitType | null }>({
    open: false,
    visitType: null,
  })

  // Guard against React strict-mode re-mounts, re-renders, and late cache
  // updates firing the defaults twice. Only fire once per step lifetime.
  const defaultsFiredRef = useRef(false)

  useEffect(() => {
    if (defaultsFiredRef.current) return
    if (!isSuccess) return
    if (!visitTypes || visitTypes.length > 0) return
    if (!provider) return

    defaultsFiredRef.current = true
    for (const defaults of PLATFORM_DEFAULT_VISIT_TYPES) {
      createVisitType.mutate({
        name: defaults.name,
        category: defaults.category,
        provider_id: provider.id,
        provider_name: provider.name ?? '',
        price_cents: defaults.price_cents,
        description: defaults.description,
        is_active: true,
      })
    }
  }, [isSuccess, visitTypes, provider, createVisitType])

  const hasActive = (visitTypes ?? []).some((vt) => vt.is_active)
  const seeding = isSuccess && (visitTypes?.length ?? 0) === 0

  const handleContinue = () => {
    setStepData(STEP_INDEX, { confirmed: true })
    markCompleted(STEP_INDEX)
    goToStep(STEP_INDEX + 1)
  }

  useForwardAction({
    label: 'Continue',
    disabled: !hasActive || createVisitType.isPending,
    onClick: handleContinue,
  })

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-slate-900">Visit types</h2>
        <p className="mt-1 text-sm text-slate-600">
          Patients choose a visit type when they book. We&apos;ve pre-filled a few
          defaults — edit, remove, or add your own.
        </p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Configured visit types</CardTitle>
          <Button
            size="sm"
            onClick={() => setModalState({ open: true, visitType: null })}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add visit type
          </Button>
        </CardHeader>
        <CardContent>
          {seeding ? (
            <p className="text-sm text-slate-500">Adding default visit types…</p>
          ) : !visitTypes || visitTypes.length === 0 ? (
            <p className="text-sm text-slate-500">
              No visit types yet. Add at least one to continue.
            </p>
          ) : (
            <div className="space-y-2">
              {visitTypes.map((vt) => (
                <button
                  key={vt.id}
                  type="button"
                  className="flex w-full items-center justify-between rounded-lg border p-4 text-left hover:bg-slate-50 transition-colors"
                  onClick={() => setModalState({ open: true, visitType: vt })}
                >
                  <div className="flex flex-col gap-1">
                    <span className="text-sm font-medium text-slate-900">{vt.name}</span>
                    {vt.description && (
                      <span className="text-xs text-slate-500 line-clamp-1">
                        {vt.description}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">
                      {formatVisitTypeCategory(vt.category)}
                    </Badge>
                    <span className="text-sm font-medium text-slate-700">
                      {formatDollars(vt.price_cents)}
                    </span>
                    <Badge variant={vt.is_active ? 'default' : 'secondary'}>
                      {vt.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <VisitTypeModal
        visitType={modalState.visitType}
        open={modalState.open}
        onClose={() => setModalState({ open: false, visitType: null })}
        showToasts={false}
      />
    </div>
  )
}
